import { useMemo } from 'react';
import {
  computeLevelBreakdown,
  fmt,
  fmtMoney,
  levelOfTab,
  type AnalyticsTab,
  type Perspective,
  type SeparationState,
} from '@integrate/core';
import { StatTile } from '../ui/StatTile.js';

/*
  One grouping level's population, measured three ways — the "By <level>" tabs.

  The arithmetic is `computeLevelBreakdown` in core, where every divergence from the frozen app is
  stated and tested. This file is the surface, and it carries two decisions of its own.

  THE PERSPECTIVE IS THE READER'S, not the chart's. The app intersected this tab with the chart's whole
  lens — perspective, the display-only grey/hide toggle, the phase-bar stage and the filter row — while
  captioning it with the perspective alone (orgchart.html:7291, :7401). So the same tab read 120 or 400
  depending on a toolbar switch behind the modal, both captioned "Carve-out view". Here it is a control,
  seeded from the chart once per opening so the tab starts on what you were looking at, and named in the
  caption. Nothing else about the chart reaches it — `computeLevelBreakdown` takes no lens, so that is a
  fact about the signature rather than a promise.

  THE PEOPLE IT LEAVES OUT ARE NAMED. Open positions, inactive people and attrition leavers are out of
  every headcount here, as they are everywhere else — but the group cards tally them beside the number
  and the app's version of this pane did not, so the one surface built to break headcount down by level
  was the one place recruitment exposure could not be seen.
*/

const PERSPECTIVES: { key: Perspective; label: string }[] = [
  { key: 'as-is', label: 'As-is' },
  { key: 'remain', label: 'RemainCo' },
  { key: 'carve-out', label: 'Carve-out' },
];

export function LevelPane({
  state,
  tab,
  perspective,
  onPerspective,
}: {
  state: SeparationState;
  tab: AnalyticsTab;
  /**
   * Owned by the modal, not by this pane.
   *
   * It was local state seeded from the chart's lens — which meant a tab switch unmounted the pane and the
   * next mount silently replaced the reader's choice with the chart's, changing every number here. The
   * seeding-from-the-chart is still deliberate; it now happens once per opening of the modal.
   */
  perspective: Perspective;
  onPerspective(next: Perspective): void;
}): JSX.Element {
  const level = levelOfTab(state, tab);

  const breakdown = useMemo(
    () => (level ? computeLevelBreakdown(state, { levelKey: level.key, perspective }) : null),
    [state, level?.key, perspective],
  );

  /*
    A level can be removed by somebody else's command while this modal is open. `resolveAnalyticsTab`
    moves the tab, so this is the frame between the command landing and the re-render — a real state,
    not an impossible one, and it says so rather than rendering an empty table.
  */
  if (!level || !breakdown) {
    return (
      <p className="muted" data-testid="level-pane-gone">
        That grouping level is no longer part of this project.
      </p>
    );
  }

  const { rows, totals, omitted } = breakdown;
  const omittedTotal = omitted.openPositions + omitted.inactive + omitted.leavers;

  return (
    <div data-testid="level-pane" data-level-key={level.key} data-perspective={perspective}>
      <div className="row" style={{ alignItems: 'center', gap: 12 }}>
        <span className="muted small">Population</span>
        <div className="tabs" role="group" aria-label="Perspective">
          {PERSPECTIVES.map((p) => (
            <button
              key={p.key}
              className={`toggle-btn${perspective === p.key ? ' active' : ''}`}
              aria-pressed={perspective === p.key}
              data-testid={`level-perspective-${p.key}`}
              onClick={() => onPerspective(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/*
        The figures as numbers as well as text: `fmt` puts thousands separators in, so the rendered
        string is not something a check should be parsing back out — and a check that silently falls
        back when the parse fails is not a check.
      */}
      <div
        className="stat-grid"
        data-testid="level-strip"
        data-hc={totals.hc}
        data-fte={totals.fte}
        data-salary={totals.salary}
        data-avg={totals.avgSalary}
      >
        <StatTile label="Headcount" valueTestId="level-hc" value={fmt(totals.hc)} sub={captionOf(perspective)} />
        <StatTile label="Total FTE" valueTestId="level-fte" value={fmt(totals.fte, 1)} sub="filled people only" />
        <StatTile
          label="Total salary"
          valueTestId="level-salary"
          value={fmtMoney(totals.salary)}
          sub="raw annual, as designed"
        />
        <StatTile
          label="Avg salary"
          valueTestId="level-avg"
          value={fmtMoney(totals.avgSalary)}
          sub={`over ${fmt(totals.hc)} ${totals.hc === 1 ? 'person' : 'people'}`}
        />
      </div>

      {/*
        Named, not dropped. Three separate tallies because they are three different things to act on: a
        requisition to fill, a person on long-term absence, and somebody already leaving.
      */}
      <p
        className="muted small"
        data-testid="level-omitted"
        data-n={omittedTotal}
        data-open={omitted.openPositions}
        data-inactive={omitted.inactive}
        data-leavers={omitted.leavers}
      >
        {omittedTotal === 0 ? (
          <>Every person in this population counts: no open positions, nobody inactive, no leavers.</>
        ) : (
          <>
            Not in these figures:{' '}
            <strong data-testid="level-omitted-open">{fmt(omitted.openPositions)}</strong> open{' '}
            {omitted.openPositions === 1 ? 'position' : 'positions'},{' '}
            <strong data-testid="level-omitted-inactive">{fmt(omitted.inactive)}</strong> inactive,{' '}
            <strong data-testid="level-omitted-leavers">{fmt(omitted.leavers)}</strong> leaving through
            attrition. Same rule as the cards and the phase bridge.
          </>
        )}
        {breakdown.buildUps > 0 && (
          <>
            {' '}
            Includes{' '}
            <strong data-testid="level-buildups" data-n={breakdown.buildUps}>
              {fmt(breakdown.buildUps)}
            </strong>{' '}
            planned{' '}
            {breakdown.buildUps === 1 ? 'hire' : 'hires'}, who do not exist as-is.
          </>
        )}
      </p>

      {rows.length === 0 ? (
        <p className="muted" data-testid="level-empty">
          Nobody is in this population, so there is nothing to break down.
        </p>
      ) : (
        <table className="data-table" data-testid="level-table">
          <thead>
            <tr>
              <th>{level.label}</th>
              <th className="num">HC</th>
              <th className="num">Total FTE</th>
              <th className="num">Total salary</th>
              <th className="num">Avg salary</th>
              {/* Only when there is a salary to take a share OF — see `anySalary`. */}
              {breakdown.anySalary && <th style={{ width: '25%' }}>Share of salary</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                /*
                  Index-testid, key-attribute: a group's name is arbitrary user data — it can contain
                  quotes, a bracket, or the ' › ' the path itself is joined with — so it makes a poor
                  selector and a fine attribute.
                */
                key={r.key}
                data-testid={`level-row-${i}`}
                data-key={r.key}
                data-hc={r.hc}
                data-fte={r.fte}
                data-salary={r.salary}
                data-share={r.salaryShare}
              >
                <td>
                  {r.ancestry.length > 0 && (
                    <span className="muted small">{r.ancestry.join(' › ')} › </span>
                  )}
                  {r.value}
                </td>
                <td className="num">{fmt(r.hc)}</td>
                <td className="num">{fmt(r.fte, 1)}</td>
                <td className="num">{fmtMoney(r.salary)}</td>
                <td className="num">{fmtMoney(r.avgSalary)}</td>
                {breakdown.anySalary && (
                  <td className="bar-cell">
                    <div
                      className="bar"
                      style={{ width: `${(r.salaryShare * 100).toFixed(1)}%` }}
                      /* The number the bar draws, so the column is readable without measuring it. */
                      title={`${(r.salaryShare * 100).toFixed(1)}% of ${fmtMoney(totals.salary)}`}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!breakdown.anySalary && rows.length > 0 && (
        <p className="muted small" data-testid="level-no-salary">
          No salary is recorded for anybody in this population, so there is no share to draw. The rows
          are ordered by headcount, not by whichever person happened to be first in the uploaded file.
        </p>
      )}
    </div>
  );
}

function captionOf(perspective: Perspective): string {
  if (perspective === 'as-is') return 'the whole organisation today';
  if (perspective === 'remain') return 'RemainCo, including undecided people';
  return 'the carve-out, at every stage';
}
